import {
  Viewer,
  BingMapsImageryProvider,
  CesiumTerrainProvider,
  TileMapServiceImageryProvider,
  WebMapServiceImageryProvider,
  Cesium3DTileset,
  GeoJsonDataSource,
  ImageryLayer,
  Cesium3DTileStyle,
  BingMapsStyle,
  Color,
  defined,
  JulianDate,
} from "cesium";
import * as Cesium from "cesium";
import { DateFormat } from "../../utils/dateFormat";
import {
  focusOnSelectedEntity,
  getLayers,
  getTerrains,
  initializeMap,
  polylineOff,
  polylineOn,
  setConvertedFeatures,
  setFileLayerVisibility,
  setObjectVisibility,
  updateArcgisToken,
  zoomIn,
  zoomOut,
} from "../actions/map";
import { LayerType } from "../../types/layer-type";
import { rulerInit } from "../../utils/cesium-ruler";
import { popupHelpers } from "../../utils/cesium-popup-helpers";
import {
  changeCoordinates,
  changeOpacity,
  changeRotationAngle,
  hiddenMapDataTypes,
  MapSection,
  setLayersVisible,
  setLoading,
  setZoom,
} from "../reducers/map";
import { LayersDataTypes } from "../../types/layers-data-types";
import { ArcgisHybridLayer } from "../../interfaces/layer";
import * as projection from "@arcgis/core/geometry/projection.js";
import { arcgisToGeoJSON } from "@esri/arcgis-to-geojson-utils";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { stages, territoryTypes } from "../reducers/utilities-journal";
import {
  getCesiumLayerKey,
  getTerritoryLayerName,
} from "../../utils/helpers-for-utilities";

export const cesiumMiddleware = (section) => {
  return (store) => {
    let viewer = null;
    let baseMap = ArcgisHybridLayer;
    const { dispatch } = store;

    const requestedHosts = new Set();
    const getTokenForLayer = (layer) => {
      if (!layer || !layer.url) return null;
      try {
        const host = new URL(layer.url).host;
        const tokens = store.getState().map.arcgisTokens || {};
        const token = tokens[host];
        const isExpired = token && token.expires < Date.now();
        if ((!token || isExpired) && !requestedHosts.has(host)) {
          requestedHosts.add(host);
          dispatch(updateArcgisToken(host));
        }
        if (token && !isExpired) {
          return token;
        }
      } catch (e) {
        return null;
      }
      return null;
    };

    function addLayers(layer) {
      if (hiddenMapDataTypes.includes(layer.dataType)) return;
      const alwaysVisibleLayer = layer.dataType === LayersDataTypes.OmanBorder;

      const visible =
        store.getState().map.visibleLayersId.includes(layer.id) ||
        store.getState().map.visibleLayersId.includes(layer.parentId) ||
        alwaysVisibleLayer;
      if (layer.dataType === LayersDataTypes.BaseMap) {
        if (visible) changeBaseMap(layer);
      } else if (
        (layer.type === LayerType.GeoJSON ||
          layer.type === LayerType.ArcgisFeatureServer) &&
        visible
      ) {
        loadGeoJSONLayer(layer);
      } else if (
        (layer.type === LayerType.Tileset ||
          layer.type === LayerType.PointCloud) &&
        visible
      ) {
        loadTilesetLayer(layer);
      } else if (layer.type === LayerType.TMS) {
        let { onLoad, ...style3D } = layer.style3D;
        style3D.name = layer.name;
        style3D.show = visible;

        var imageryLayer = ImageryLayer.fromProviderAsync(
          TileMapServiceImageryProvider.fromUrl(layer.url, style3D),
        );
        imageryLayer.readyEvent.addEventListener((error) => {
          navigator.locks.request("add_layer_datasource", () =>
            dataSources.set(layer.name, imageryLayer),
          );
          imageryLayer.show = visible;
          imageryLayer.alpha = 1.0;
          viewer.imageryLayers.add(imageryLayer);
        });
        imageryLayer.errorEvent.addEventListener((error) => {
          console.log(`There was an error creating the tms layer. ${error}`);
        });
      } else if (layer.type === LayerType.WMS) {
        let { onLoad, ...style3D } = layer.style3D;
        style3D.name = layer.name;
        style3D.show = visible;

        var imageryLayer = ImageryLayer.fromProviderAsync(
          new WebMapServiceImageryProvider(style3D),
        );
        imageryLayer.readyEvent.addEventListener((error) => {
          navigator.locks.request("add_layer_datasource", () =>
            dataSources.set(layer.name, imageryLayer),
          );
          imageryLayer.show = visible;
          viewer.imageryLayers.add(imageryLayer);
        });
        imageryLayer.errorEvent.addEventListener((error) => {
          console.log(`There was an error creating the wms layer. ${error}`);
        });
      }
    }

    function loadGeoJSONLayer(layer) {
      let { query, onLoad, ...style3D } = layer.style3D;

      // Check if this layer should use 3D tiles instead of runtime GeoJSON
      if (style3D.type === "3dtiles") {
        // Load as 3D tileset for better performance
        const tilesBaseUrl = process.env.REACT_APP_TILES_URL || '/tiles';
        const tilesetUrl = `${tilesBaseUrl}/${layer.name}/tileset.json`;
        loadTilesetLayer({
          ...layer,
          url: tilesetUrl,
          type: LayerType.Tileset,
          style3D: { ...style3D, name: layer.name, show: true },
        });
        return;
      }

      let url = layer.url;
      style3D.name = layer.name;
      style3D.show = true;
      if (layer.type === LayerType.ArcgisFeatureServer) {
        url +=
          query ??
          "/query?where=1%3D1&f=geojson&returnGeometry=true&returnZ=true&outFields=*";
        const token = getTokenForLayer(layer);
        if (token) url += `&token=${token.token}`;
      }

      GeoJsonDataSource.load(url, style3D)
        .then((d) => {
          d.name = layer.name;
          let ds = viewer.dataSources.getByName(layer.name);
          if (ds.length === 0) {
            d.name = layer.name;
            viewer.dataSources.add(d);
            d.entities.values.forEach((entity) => {
              if (entity.polygon) {
                if (style3D.fill) {
                  entity.polygon = {
                    hierarchy: entity.polygon.hierarchy.getValue(),
                    material: Color.fromCssColorString(style3D.fill),
                  };
                }
                if (style3D.stroke) {
                  entity.polyline = {
                    positions: entity.polygon.hierarchy.getValue().positions,
                    width: style3D.strokeWidth ?? 1,
                    material: Color.fromCssColorString(style3D.stroke),
                    clampToGround: true,
                  };
                }
              }
            });
            if (onLoad) onLoad(d);
          } else {
            ds.slice(1).forEach((e) => viewer.dataSources.remove(e));
          }
        })
        .catch((error) => {
          console.log("Error loading geojson: " + error);
        })
        .finally(() => {
          dispatch(setLoading({ ...layer, loading: false }));
        });
    }
    function loadTilesetLayer(layer) {
      let { onLoad, ...style3D } = layer.style3D;
      style3D.name = layer.name;
      style3D.show = true;
      Cesium3DTileset.fromUrl(layer.url, style3D)
        .then((d) => {
          if (layer.type === LayerType.PointCloud) {
            d.pointCloudShading.attenuation = true;
            if (style3D.style) {
              d.style = new Cesium3DTileStyle(style3D.style);
            }
          } else {
            let alpha = layer.opacity;
            d.style = new Cesium3DTileStyle({
              color:
                "undefined === ${COLOR}.x ? color('white', " +
                alpha +
                " ) : rgba(${COLOR}.r * 255, ${COLOR}.g * 255, ${COLOR}.b *255, " +
                alpha +
                ")",
            });
          }
          primitives.set(layer.name, viewer.scene.primitives.add(d));
        })
        .catch((error) => {
          console.log("Error loading tileset: " + error);
        })
        .finally(() => {
          dispatch(setLoading({ ...layer, loading: false }));
        });
    }

    // function selectEntity(selectedEntity) {
    //   popupHelpers(store, viewer, selectedEntity);

    //   //Снимаем окраску с предыдущего и окрашиваем выделенный
    //   if (defined(selectedEntity) && selectedEntity.properties) {
    //     let layer = store
    //       .getState()
    //       .features.layers.find(
    //         (layer) =>
    //           layer.name === selectedEntity.entityCollection.owner.name,
    //       );
    //     if (layer === undefined) return;
    //     let featureDescription = selectedEntity.properties.getValue(
    //       new JulianDate(),
    //     );
    //     let description = {};

    //     //Получаем слой из описания
    //     let layerDesc = store
    //       .getState()
    //       .features.layerDescriptions.layers.find(
    //         (element) => element.name === layer.name,
    //       );

    //     if (layerDesc)
    //       layerDesc.fields.forEach((field) => {
    //         if (field.domain !== null) {
    //           let domainValue = field.domain.codedValues.find(
    //             (v) => v.code === featureDescription[field.name],
    //           );
    //           description[field.name] = domainValue
    //             ? domainValue.name
    //             : featureDescription[field.name];
    //         } else if (field.type === "esriFieldTypeDate")
    //           description[field.name] = DateFormat.format.date(
    //             new Date(featureDescription[field.name]),
    //             "MM-dd-yyyy",
    //           );
    //         else description[field.name] = featureDescription[field.name];
    //       });
    //     else description = featureDescription;

    //   }
    // }
    let dataSources = new Map();
    let primitives = new Map();

    function updateLayerVisible(layer) {
      const alwaysVisibleLayer = layer.dataType === LayersDataTypes.OmanBorder;

      const visible =
        store.getState().map.visibleLayersId.includes(layer.id) ||
        store.getState().map.visibleLayersId.includes(layer.parentId) ||
        alwaysVisibleLayer;
      if (layer.dataType === LayersDataTypes.BaseMap) {
        if (visible) changeBaseMap(layer);
      } else if (
        layer.type === LayerType.GeoJSON ||
        layer.type === LayerType.ArcgisFeatureServer
      ) {
        if (visible) loadGeoJSONLayer(layer);
        else {
          viewer.dataSources
            .getByName(layer.name)
            .forEach((d) => viewer.dataSources.remove(d));
          viewer.dataSources.remove(layer.dataSource);
          dispatch(setLoading({ ...layer, loading: false }));
        }
      } else if (
        layer.type === LayerType.Tileset ||
        layer.type === LayerType.PointCloud
      ) {
        if (visible && !primitives.has(layer.name)) loadTilesetLayer(layer);
        else {
          let primitive = primitives.get(layer.name);
          if (primitive) {
            primitive.show = visible;
          }
          dispatch(setLoading({ ...layer, loading: false }));
        }
      } else {
        if (dataSources.has(layer.name) && dataSources.get(layer.name).show !==visible) {
          dataSources.get(layer.name).show = visible;
        }
        dispatch(setLoading({ ...layer, loading: false }));
      }
    }
    let rulerControls;
    function changeBaseMap(layer) {
      if (baseMap === layer)
        return;
      baseMap = layer
      viewer.imageryLayers.remove(viewer.imageryLayers.get(0));
      viewer.imageryLayers.addImageryProvider(GetTileBaseLayer(layer.url), 0);
    }
    function GetTileBaseLayer(url) {
      return new Cesium.UrlTemplateImageryProvider({
        url: url
          .replace("{col}", "{x}")
          .replace("{row}", "{y}")
          .replace("{level}", "{z}")
          .replace("{lang}", navigator.language),
      });
    }
    let zoomLevelHeights;
    function getZoomLevelHeights(precision) {
      precision = precision || 10;

      var step = 100000.0;

      var result = [];
      var currentZoomLevel = 0;
      for (var height = 100000000.0; height > step; height = height - step) {
        var level = detectZoomLevel(height);
        if (level === null) {
          break;
        }

        if (level !== currentZoomLevel) {
          var minHeight = height;
          var maxHeight = height + step;
          while (maxHeight - minHeight > precision) {
            height = minHeight + (maxHeight - minHeight) / 2;
            if (detectZoomLevel(height) === level) {
              minHeight = height;
            } else {
              maxHeight = height;
            }
          }

          result.push({
            level: level,
            height: Math.round(height),
          });

          currentZoomLevel = level;

          if (result.length >= 2) {
            step = (result[result.length - 2].height - height) / 1000.0;
          }
        }
      }

      return result;
    }
    function detectZoomLevel(distance) {
      var scene = viewer.scene;
      var tileProvider = scene.globe._surface.tileProvider;
      var quadtree = tileProvider._quadtree;
      var drawingBufferHeight = viewer.canvas.height;
      var sseDenominator = viewer.camera.frustum.sseDenominator;

      for (var level = 0; level <= 19; level++) {
        var maxGeometricError =
          tileProvider.getLevelMaximumGeometricError(level);
        var error =
          (maxGeometricError * drawingBufferHeight) /
          (distance * sseDenominator);
        if (error < quadtree.maximumScreenSpaceError) {
          return level + 1;
        }
      }

      return null;
    }
    function getZoomLevel() {
      return zoomLevelHeights.find(
        (item) => item.height > viewer.camera.positionCartographic.height,
      )?.level;
    }
    function getHeightByZoom(zoom) {
      return zoomLevelHeights.find((item) => item.level == zoom)?.height;
    }

    function AddConvertedObjectOnMap() {
      const {
        convertedGeometry,
        fileHidden,
        stageTerritoryGeometry,
        buildingTerritoryGeometry,
        hiddenTerritories,
        drawnGraphics,
      } = store.getState().masterUtilities;

      const expectedKeys = new Set(
        stages.flatMap((stage) => {
          const geometry = convertedGeometry[stage];
          return geometry
            ? Object.keys(geometry).map((fileName) =>
                getCesiumLayerKey(stage, fileName),
              )
            : [];
        }),
      );

      for (let i = viewer.dataSources.length - 1; i >= 0; i--) {
        const dataSource = viewer.dataSources.get(i);
        if (
          dataSource.name?.startsWith("tmp_") &&
          !expectedKeys.has(dataSource.name)
        ) {
          viewer.dataSources.remove(dataSource);
        }
      }

      stages.forEach((stage) => {
        const stageGeometry = convertedGeometry[stage];
        if (!stageGeometry) return;

        Object.entries(stageGeometry).forEach(async ([fileName, fileData]) => {
          await projection.load();

          const features = fileData.graphics.map((item) => {
            let geometry = JSON.parse(item.geometry);
            geometry = projection.project(
              geometry,
              new SpatialReference({ wkid: 4326 }),
            );

            return {
              geometry: geometry,
              attributes: item.attributes,
            };
          });

          const geojson = arcgisToGeoJSON({ features: features });
          const blob = new Blob([JSON.stringify(geojson)], {
            type: "application/json",
          });

          const url = URL.createObjectURL(blob);
          const layerKey = getCesiumLayerKey(stage, fileName);
          const isHidden = fileHidden?.[stage]?.[fileName] ?? false;

          const existingDataSources = viewer.dataSources.getByName(layerKey);
          if (existingDataSources.length) {
            existingDataSources.forEach((ds) => viewer.dataSources.remove(ds));
          }

          GeoJsonDataSource.load(url).then((graphic) => {
            graphic.name = layerKey;
            graphic.show = !isHidden;

            const fillColor = Cesium.Color.BLACK.withAlpha(0.25);
            const lineColor = Cesium.Color.BLACK;
            const pointColor = Cesium.Color.BLACK;

            graphic.entities.values.forEach((entity) => {
              if (Cesium.defined(entity.polygon)) {
                entity.polygon.material = fillColor;
                entity.polygon.outline = true;
                entity.polygon.outlineColor = lineColor;
              } else if (Cesium.defined(entity.polyline)) {
                entity.polyline.material = lineColor;
                entity.polyline.width = 1;
              } else if (Cesium.defined(entity.position)) {
                entity.billboard = undefined;
                entity.point = new Cesium.PointGraphics({
                  pixelSize: 8,
                  color: pointColor,
                });
              }
            });

            viewer.dataSources.add(graphic);
          });
        });
      });

      territoryTypes.forEach((type) => {
        const geometries =
          type === "stage" ? stageTerritoryGeometry : buildingTerritoryGeometry;
        const hiddenFlags = hiddenTerritories[type];

        Object.entries(geometries).forEach(([stage, graphic]) => {
          if (!graphic) return;

          let geometry = graphic.geometry;

          if (geometry.spatialReference?.wkid !== 4326) {
            try {
              geometry = projection.project(
                geometry,
                new SpatialReference({ wkid: 4326 }),
              );
            } catch (error) {
              console.error(error);
              return;
            }
          }

          const geojson = arcgisToGeoJSON({
            features: [
              {
                geometry: geometry,
                attributes: graphic.attributes || {},
              },
            ],
          });

          const blob = new Blob([JSON.stringify(geojson)], {
            type: "application/json",
          });
          const url = URL.createObjectURL(blob);

          const layerName = getTerritoryLayerName(type, stage);
          const existingDataSource = viewer.dataSources.getByName(layerName);
          existingDataSource.forEach((ds) => viewer.dataSources.remove(ds));

          GeoJsonDataSource.load(url).then((dataSource) => {
            dataSource.name = layerName;
            dataSource.show = !hiddenFlags?.[stage];

            const fillColor =
              Cesium.Color.fromCssColorString("#2E1A47").withAlpha(0.1);
            const outlineColor = Cesium.Color.fromCssColorString("#2E1A47");

            dataSource.entities.values.forEach((entity) => {
              entity.polygon.material = fillColor;
              entity.polygon.outlineColor = outlineColor;
            });
            viewer.dataSources.add(dataSource);
          });
        });
      });

      if (drawnGraphics.length > 0) {
        const layerName = "drawn_objects_layer";
        const existing = viewer.dataSources.getByName(layerName);
        existing.forEach((ds) => viewer.dataSources.remove(ds));

        projection.load().then(() => {
          const features = drawnGraphics
            .filter((graphic) => graphic.geometry?.type === "polygon")
            .map((graphic) => {
              let geometry = graphic.geometry;

              if (geometry.spatialReference?.wkid !== 4326) {
                try {
                  geometry = projection.project(
                    geometry,
                    new SpatialReference({ wkid: 4326 }),
                  );
                } catch (error) {
                  console.error(error);
                  return;
                }
              }

              return {
                geometry: geometry,
                attributes: graphic.attributes || {},
              };
            })
            .filter(Boolean);

          if (features.length === 0) return;

          const geojson = arcgisToGeoJSON({ features });
          const blob = new Blob([JSON.stringify(geojson)], {
            type: "application/json",
          });
          const url = URL.createObjectURL(blob);

          GeoJsonDataSource.load(url).then((dataSource) => {
            dataSource.name = layerName;
            dataSource.show = true;

            const fillColor =
              Cesium.Color.fromCssColorString("#FAFAFA").withAlpha(0.2);
            const outlineColor = Cesium.Color.WHITE;

            dataSource.entities.values.forEach((entity) => {
              if (entity.polygon) {
                entity.polygon.material = fillColor;
                entity.polygon.outline = true;
                entity.polygon.outlineColor = outlineColor;
              }
            });

            viewer.dataSources.add(dataSource);
          });
        });
      }
    }

    return (next) => async (action) => {
      if (initializeMap.match(action)) {
        if (
          action.payload.section === section &&
          action.payload.mode === "3d"
        ) {
          if (viewer === null) {
            await next(action);
            const { terrainsLoaded, terrains } = store.getState().terrain;
            const { coordinates, zoom } = store.getState().map;
            let terrainProvider;
            if (!terrainsLoaded) {
              dispatch(getTerrains());
            } else if (terrains && terrains.length) {
              terrainProvider = await CesiumTerrainProvider.fromUrl(
                terrains.find((t) => t.visible).url,
              );
            }
            viewer = new Viewer(`${section}MapContainer`, {
              terrainProvider: terrainProvider,
              baseLayer: Cesium.ImageryLayer.fromProviderAsync(
                GetTileBaseLayer(ArcgisHybridLayer.url),
              ),
              imageryProvider: new BingMapsImageryProvider({
                url: "https://dev.virtualearth.net",
                key: "",
                mapStyle: BingMapsStyle.AERIAL_WITH_LABELS,
              }),
              baseLayerPicker: false,
              selectionIndicator: false,
              infoBox: false,
              navigationHelpButton: false,
              sceneModePicker: false,
              homeButton: false,
              animation: false,
              timeline: false,
            });
            viewer._cesiumWidget._creditContainer.style.display = "none";
            zoomLevelHeights = getZoomLevelHeights(1).reverse();
            viewer.camera.setView({
              destination: Cesium.Cartesian3.fromDegrees(
                coordinates.longitude,
                coordinates.latitude,
                getHeightByZoom(zoom),
              ),
            });

            viewer.camera.changed.addEventListener(function () {
              dispatch(
                changeCoordinates({
                  latitude: +Cesium.Math.toDegrees(
                    viewer.camera.positionCartographic.latitude,
                  ).toFixed(6),
                  longitude: +Cesium.Math.toDegrees(
                    viewer.camera.positionCartographic.longitude,
                  ).toFixed(6),
                }),
              );
              let zoom = getZoomLevel();
              if (zoom) {
                dispatch(setZoom({ zoom: zoom }));
              }
            });
            const mapElement = document.getElementById(
              `${section}MapContainer`,
            );

            const handler = new Cesium.ScreenSpaceEventHandler(
              viewer.scene.canvas,
            );

            let isDragging = false;

            handler.setInputAction(function (movement) {
              isDragging = true;
              mapElement.style.cursor = "grabbing";
            }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

            handler.setInputAction(function (movement) {
              if (isDragging) {
                isDragging = false;
                mapElement.style.cursor = "auto";
              }
            }, Cesium.ScreenSpaceEventType.LEFT_UP);

            //viewer.selectedEntityChanged.addEventListener(selectEntity);
            let { tokenLoading } = store.getState().map;
            rulerControls = rulerInit(viewer);
            if (!tokenLoading) {
              store.getState().map.layers.forEach((e) => addLayers(e));
            }
            if (section === MapSection.utilities) AddConvertedObjectOnMap();
          }
          return;
        } else if (viewer !== null) {
          viewer.destroy();
          viewer = null;
        }
      }
      if (viewer === null || store.getState().map.section !== section) {
        return next(action);
      }
      if (getTerrains.fulfilled.match(action)) {
        next(action);
        const { terrains } = store.getState().terrain;
        if (terrains && terrains.length) {
          viewer.terrainProvider = await CesiumTerrainProvider.fromUrl(
            terrains.find((t) => t.visible).url,
          );
        }
        return;
      }
      if (focusOnSelectedEntity.match(action)) {
        if (defined(viewer.selectedEntity)) viewer.flyTo(viewer.selectedEntity);
      }
      if (
        setLayersVisible.match(action) ||
        updateArcgisToken.fulfilled.match(action)
      ) {
        next(action);
        if (
          updateArcgisToken.fulfilled.match(action) &&
          action.meta?.arg
        ) {
          requestedHosts.delete(action.meta.arg);
        }
        if (store.getState().map.tokenLoading) return;
        store
          .getState()
          .map.layers.forEach((l) => updateLayerVisible(l));
        return;
      }

      if (polylineOn.match(action)) {
        rulerControls.on();
      }

      if (polylineOff.match(action)) {
        rulerControls.off();
      }

      if (changeRotationAngle.match(action)) {
        next(action);
        const prevRotation = store.getState().map.rotationAngle.prev;
        const newRotation = action.payload;

        if (prevRotation < newRotation) {
          viewer.camera.twistRight(
            Cesium.Math.toRadians(newRotation - prevRotation),
          );
        } else {
          viewer.camera.twistLeft(
            Cesium.Math.toRadians(prevRotation - newRotation),
          );
        }
      }
      if (zoomIn.match(action)) {
        let zoom = getZoomLevel();
        const step =
          viewer.camera.positionCartographic.height -
          getHeightByZoom(zoom + 1) +
          1;
        viewer.camera.zoomIn(step);
      }
      if (zoomOut.match(action)) {
        let zoom = getZoomLevel();
        const step =
          viewer.camera.positionCartographic.height -
          getHeightByZoom(zoom - 1) +
          1;
        viewer.camera.zoomOut(step);
      }
      if (changeOpacity.match(action) && dataSources.has(action.payload.name)) {
        if (action.payload.type === LayerType.Tileset) {
          let alpha = action.payload.opacity;
          dataSources.get(action.payload.name).style = new Cesium3DTileStyle({
            color:
              "undefined === ${COLOR}.x ? color('white', " +
              alpha +
              " ) : rgba(${COLOR}.r * 255, ${COLOR}.g * 255, ${COLOR}.b *255, " +
              alpha +
              ")",
          });
        }
        if (action.payload.type === LayerType.TMS) {
          dataSources.get(action.payload.name).alpha = action.payload.opacity;
        }
      }

      if (setFileLayerVisibility.match(action)) {
        const { fileName, visible, stage } = action.payload;
        const layerKey = getCesiumLayerKey(stage, fileName);
        const matchingDataSources = viewer.dataSources.getByName(layerKey);

        matchingDataSources.forEach((dataSource) => {
          dataSource.show = visible;
        });

        return;
      }

      if (setObjectVisibility.match(action)) {
        const { hiddenTerritories } = store.getState().masterUtilities;

        territoryTypes.forEach((type) => {
          Object.entries(hiddenTerritories[type]).forEach(
            ([stage, isHidden]) => {
              const layerName = getTerritoryLayerName(type, stage);
              const dsArray = viewer.dataSources.getByName(layerName);

              dsArray.forEach((ds) => {
                ds.show = !isHidden;
              });
            },
          );
        });

        return;
      }

      if (getLayers.fulfilled.match(action)) {
        next(action);
        store.getState().map.layers.forEach((e) => addLayers(e));
        return;
      }

      if (
        setConvertedFeatures.match(action) &&
        section === MapSection.utilities
      ) {
        AddConvertedObjectOnMap();
      }
      next(action);
    };
  };
};
